import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import PremisesSelect from './PremisesSelect';
import AggNodeSelect from './AggNodeSelect';
import '../styles/structure-form.css';
const SPLIT_WAYS_OPTIONS = [2, 4, 8, 16, 32];
export const PremisesBoxDialog = ({ open, onClose, structureId, easting, northing, plantItem, onSuccess, }) => {
    const isCtb = plantItem === 'CTB';
    const label = isCtb ? 'CTB' : 'Toby Box';
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [uprn, setUprn] = useState('');
    const [inventoryStatusCode, setInventoryStatusCode] = useState('IPL');
    const [splitWays, setSplitWays] = useState('8');
    const [nodeDefaultUs, setNodeDefaultUs] = useState('');
    const [servedUprns, setServedUprns] = useState([]);
    const [premises, setPremises] = useState([]);
    const loadedUprn = useRef(null);
    // Load the box's current state, then the premises list around it.
    useEffect(() => {
        if (!open)
            return;
        const controller = new AbortController();
        setLoading(true);
        setError(null);
        (async () => {
            try {
                const boxResponse = await fetch(`/structure/${structureId}/premises-box`, {
                    headers: { Accept: 'application/json' },
                    signal: controller.signal,
                });
                const box = await boxResponse.json().catch(() => ({}));
                if (!boxResponse.ok) {
                    throw new Error(box.error || `Unable to load the ${label}`);
                }
                const currentServed = box.served_uprns || [];
                loadedUprn.current = box.uprn ? String(box.uprn) : '';
                setUprn(box.uprn ? String(box.uprn) : '');
                setInventoryStatusCode(box.inventory_status_code || 'IPL');
                setSplitWays(box.split_ways ? String(box.split_ways) : '');
                setNodeDefaultUs(box.node_default_us || '');
                setServedUprns(currentServed);
                if (isCtb) {
                    const params = new URLSearchParams({
                        easting: String(easting),
                        northing: String(northing),
                        limit: '25',
                    });
                    if (currentServed.length)
                        params.set('selectedUprns', currentServed.join(','));
                    const premisesResponse = await fetch(`/structure/nearest-premises?${params.toString()}`, {
                        headers: { Accept: 'application/json' },
                        signal: controller.signal,
                    });
                    const result = await premisesResponse.json().catch(() => ({}));
                    if (!premisesResponse.ok) {
                        throw new Error(result.error || 'Unable to load nearby premises');
                    }
                    const extra = result.selectedPremises || [];
                    const nearest = result.premises || [];
                    const seen = new Set(nearest.map(premise => premise.uprn));
                    setPremises([...extra.filter(premise => !seen.has(premise.uprn)), ...nearest]);
                }
            }
            catch (loadError) {
                if (loadError.name !== 'AbortError') {
                    setError(loadError instanceof Error ? loadError.message : 'Unable to load the box');
                }
            }
            finally {
                if (!controller.signal.aborted)
                    setLoading(false);
            }
        })();
        return () => controller.abort();
    }, [open, structureId, easting, northing, isCtb, label]);
    // The host premises is served by default, but can be unticked — a CTB may be
    // bolted to one house and feed only its neighbours.
    useEffect(() => {
        if (!isCtb || !uprn)
            return;
        // On open, the server's saved served-set wins — the host may have been
        // deliberately unticked. Only pre-tick when the user picks a NEW host.
        if (uprn === loadedUprn.current)
            return;
        setServedUprns(current => (current.includes(uprn) ? current : [...current, uprn]));
    }, [isCtb, uprn]);
    const toggleServed = (candidate) => {
        setServedUprns(current => current.includes(candidate)
            ? current.filter(entry => entry !== candidate)
            : [...current, candidate]);
    };
    const handleSave = async () => {
        setSaving(true);
        setError(null);
        try {
            const body = {
                uprn: uprn || null,
                inventory_status_code: inventoryStatusCode,
            };
            if (isCtb) {
                body.served_uprns = servedUprns;
                body.split_ways = splitWays ? Number(splitWays) : null;
                body.node_default_us = nodeDefaultUs || null;
            }
            const response = await fetch(`/structure/${structureId}/premises-box`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
                body: JSON.stringify(body),
            });
            const result = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(result.error || `Unable to update the ${label}`);
            }
            onSuccess?.();
            onClose();
        }
        catch (saveError) {
            setError(saveError instanceof Error ? saveError.message : `Unable to update the ${label}`);
        }
        finally {
            setSaving(false);
        }
    };
    const ways = Number(splitWays) || 0;
    return (_jsx(Dialog, { open: open, onOpenChange: isOpen => !isOpen && onClose(), children: _jsxs(DialogContent, { children: [_jsxs(DialogHeader, { children: [_jsxs(DialogTitle, { children: ["Edit ", label] }), _jsx(DialogDescription, { children: isCtb
                                ? 'Set its status, the house it is attached to, its splitter, and the premises it serves.'
                                : 'Set its installation status and optionally select one of the nearest premises.' })] }), _jsxs("div", { className: "structure-form__fields", children: [_jsxs("div", { className: "structure-form__field", children: [_jsxs("label", { htmlFor: "premises-box-status", className: "structure-form__label", children: ["Status", _jsx("span", { className: "structure-form__required", children: "*" })] }), _jsx("div", { className: "structure-form__input-wrapper", children: _jsxs("select", { id: "premises-box-status", name: "inventory_status_code", className: "structure-form__input structure-form__select", value: inventoryStatusCode, onChange: event => setInventoryStatusCode(event.target.value), disabled: saving || loading, required: true, children: [_jsx("option", { value: "PDA", children: "Pending Addition" }), _jsx("option", { value: "IPL", children: "Installed" })] }) })] }), _jsxs("div", { className: "structure-form__field", children: [_jsx("label", { htmlFor: "premises-box-uprn", className: "structure-form__label", children: isCtb ? 'Host Premises' : 'Premises' }), _jsx(PremisesSelect, { id: "premises-box-uprn", name: "uprn", easting: easting, northing: northing, value: uprn, selectedUprn: uprn || undefined, onChange: setUprn, disabled: saving || loading })] }), isCtb && (_jsxs(_Fragment, { children: [_jsxs("div", { className: "structure-form__field", children: [_jsx("label", { htmlFor: "premises-box-ways", className: "structure-form__label", children: "Splitter Ways" }), _jsx("div", { className: "structure-form__input-wrapper", children: _jsxs("select", { id: "premises-box-ways", name: "split_ways", className: "structure-form__input structure-form__select", value: splitWays, onChange: event => setSplitWays(event.target.value), disabled: saving || loading, children: [_jsx("option", { value: "", children: "No splitter" }), SPLIT_WAYS_OPTIONS.map(option => (_jsx("option", { value: String(option), children: `1:${option}` }, option)))] }) })] }), _jsxs("div", { className: "structure-form__field", children: [_jsx("label", { htmlFor: "premises-box-upstream", className: "structure-form__label", children: "Fed From" }), _jsx(AggNodeSelect, { autoDefault: false, id: "premises-box-upstream", name: "node_default_us", easting: easting, northing: northing, value: nodeDefaultUs, selectedNodeId: nodeDefaultUs || undefined, onChange: setNodeDefaultUs, disabled: saving || loading })] }), _jsxs("div", { className: "structure-form__field", children: [_jsxs("label", { className: "structure-form__label", children: ["Premises Served", _jsx("span", { className: "structure-form__hint", children: ways
                                                        ? ` ${servedUprns.length} of ${ways} ways used`
                                                        : ` ${servedUprns.length} served, no splitter` })] }), _jsxs("div", { className: "structure-form__input-wrapper", children: [loading && _jsx("div", { children: "Loading nearby premises\u2026" }), !loading && premises.map(premise => (_jsxs("label", { className: "structure-form__checkbox-row", children: [_jsx("input", { type: "checkbox", checked: servedUprns.includes(premise.uprn), onChange: () => toggleServed(premise.uprn), disabled: saving }), _jsxs("span", { children: [premise.address, " (", premise.uprn, ")", premise.uprn === uprn ? ' — host' : ''] })] }, premise.uprn)))] })] })] })), error && _jsx("div", { className: "structure-form__error", children: error })] }), _jsxs(DialogFooter, { children: [_jsx(Button, { type: "button", variant: "outline", onClick: onClose, disabled: saving, children: "Cancel" }), _jsx(Button, { type: "button", onClick: handleSave, disabled: saving || loading, children: saving ? 'Saving…' : 'Save' })] })] }) }));
};
export default PremisesBoxDialog;
